import React, { useState, useEffect, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { apiService } from "../api/apiService";
import styles from "./SygnalistaPage.module.css";
import NaglowekPodstrony from "../components/common/NaglowekPodstrony";
import Informator from "../components/common/Informator/Informator";
import {
    FiShield,
    FiExternalLink,
    FiLock,
    FiEye,
    FiEyeOff,
    FiAlertTriangle,
    FiChevronDown,
    FiGlobe,
    FiFileText,
    FiUsers,
    FiInfo,
    FiHelpCircle,
    FiPhone,
} from "react-icons/fi";
import { fadeUp } from "../utils/animationVariants";
import { shakeModal } from "../utils/modalAttention";
import { useToast } from "../context/ToastContext";
import { FiCopy, FiX, FiArrowRight } from "react-icons/fi";

const COUNTDOWN_SECONDS = 5;
/** Jedno źródło adresu platformy — był zaszyty w dwóch miejscach. */
const PLATFORM_URL = "https://sygnalista.krosno.com";

const SygnalistaPage = () => {
    const { showToast } = useToast();
    // [FIRMA] Nazwa i dane rejestrowe pracodawcy pochodzą z konfiguracji aplikacji
    // (panel admina → „Microsoft 365 — integracje i skrzynki" → „Skrzynki i tożsamość"),
    // wyznaczane po firmie PRZYPISANEJ do zalogowanego użytkownika. Wcześniej były zaszyte
    // w kodzie, więc każda dzierżawa widziała „Krosno Glass S.A." niezależnie od ustawień.
    const [company, setCompany] = useState(null);

    /* [DWS-01-a, poprawka przeglądu bezpieczeństwa 2026-09-26] DANE FIRMY SPOD PREFIKSU KANAŁU.
       BYŁO ŹLE: strona wołała `GET /api/company/my`, a ten adres wołają tylko ona i jednorazowy onboarding — dziennik
       aktywności zapisywał go z UserId i sekundą, więc analityka czasu pracy pokazywała, kto i kiedy wszedł w kanał
       zgłoszeń (mimo że puls strony już milczał). NAPRAWA: każde żądanie tej strony idzie pod `/api/sygnalista/`
       (poza dziennikiem aktywności, `access_log off` w nginx). Pilnuje tego SygnalistaPage.bezSladu.dws01a.test.js. */
    useEffect(() => {
        let cancelled = false;
        apiService
            .getSygnalistaFirma()
            .then((res) => { if (!cancelled) setCompany(res.data || null); })
            .catch((err) => console.error("[SygnalistaPage] Nie udało się pobrać danych firmy:", err));
        return () => { cancelled = true; };
    }, []);

    /** Nazwa firmy; do czasu wczytania używamy neutralnego określenia, nigdy zaszytej nazwy. */
    const companyName = company?.name || "Spółka";

    /** Wiersz danych rejestrowych do stopki prawnej — pomija pola, których firma nie ma. */
    const companyLegalLine = [
        company?.name,
        company?.address,
        company?.nip ? `NIP: ${company.nip}` : null,
        company?.krs ? `KRS: ${company.krs}` : null,
        company?.regon ? `REGON: ${company.regon}` : null,
    ].filter(Boolean).join(" | ");
    const [expandedSection, setExpandedSection] = useState(null);
    const [showRedirectOverlay, setShowRedirectOverlay] = useState(false);
    const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
    // [UX 2026-07-29] Gdy przeglądarka zablokuje automatyczne otwarcie (popup po timerze),
    // pokazujemy w oknie widoczny link — inaczej użytkownik czekał 5 s i „nic się nie działo".
    const [popupBlocked, setPopupBlocked] = useState(false);
    const countdownRef = useRef(null);
    const overlayCardRef = useRef(null);

    const toggleSection = useCallback((key) => {
        setExpandedSection((prev) => (prev === key ? null : key));
    }, []);

    // Countdown logic
    useEffect(() => {
        if (!showRedirectOverlay) return;

        setCountdown(COUNTDOWN_SECONDS);
        countdownRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, [showRedirectOverlay]);

    // Po odliczeniu próbujemy otworzyć platformę; blokada popupów NIE zostawia użytkownika
    // z pustym ekranem — okno zostaje otwarte z widocznym linkiem awaryjnym.
    useEffect(() => {
        if (showRedirectOverlay && countdown === 0) {
            const timer = setTimeout(() => {
                const opened = openExternalLink();
                if (opened) setShowRedirectOverlay(false);
                else setPopupBlocked(true);
            }, 400);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countdown, showRedirectOverlay]);

    // Escape zamyka okno przekierowania (W-10: zamykamy przyciskami/Esc, nigdy kliknięciem w tło).
    useEffect(() => {
        if (!showRedirectOverlay) return;
        const onKey = (e) => { if (e.key === "Escape") handleCancelRedirect(); };
        document.addEventListener("keydown", onKey);
        overlayCardRef.current?.focus?.();
        return () => document.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showRedirectOverlay]);

    const handleOpenPlatform = useCallback(() => {
        setPopupBlocked(false);
        setShowRedirectOverlay(true);
    }, []);

    const handleCancelRedirect = useCallback(() => {
        setShowRedirectOverlay(false);
        setPopupBlocked(false);
        if (countdownRef.current) clearInterval(countdownRef.current);
    }, []);

    /**
     * Otwiera platformę i zapisuje ślad audytowy. Kolejność jest istotna: window.open MUSI
     * pójść synchronicznie (przed await), inaczej przeglądarka traktuje je jako popup i blokuje.
     * Zwraca true, gdy okno faktycznie się otworzyło.
     */
    const openExternalLink = useCallback(() => {
        const win = window.open(PLATFORM_URL, "_blank", "noopener,noreferrer");
        apiService.logSygnalistaClick().catch((e) => console.error("Sygnalista log error:", e));
        return !!win;
    }, []);

    /** „Przejdź teraz" — pominięcie odliczania (kliknięcie = gest użytkownika, brak blokady). */
    const handleGoNow = useCallback(() => {
        if (countdownRef.current) clearInterval(countdownRef.current);
        if (openExternalLink()) setShowRedirectOverlay(false);
        else setPopupBlocked(true);
    }, [openExternalLink]);

    const handleCopyUrl = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(PLATFORM_URL);
            showToast("Adres skopiowany — wklej go w oknie prywatnym przeglądarki.", "success");
        } catch {
            showToast(`Skopiuj ręcznie: ${PLATFORM_URL}`, "info");
        }
    }, [showToast]);

    const infoSections = [
        {
            key: "what",
            icon: <FiInfo size={18} />,
            title: "Czym jest platforma Sygnalista?",
            content: (
                <>
                    <p>
                        Platforma Sygnalista to wewnętrzny kanał zgłaszania naruszeń prawa, wdrożony
                        w <strong>{companyName}</strong> zgodnie z ustawą z dnia 14 czerwca 2024 r.
                        o ochronie sygnalistów (Dz. U. z 2024 r. poz. 928).
                    </p>
                    <p>
                        Platforma umożliwia pracownikom oraz innym uprawnionym osobom dokonywanie
                        zgłoszeń naruszeń prawa, zapewniając jednocześnie pełną ochronę przed
                        działaniami odwetowymi.
                    </p>
                </>
            ),
        },
        {
            key: "anonymity",
            icon: <FiEyeOff size={18} />,
            title: "Gwarancja anonimowości",
            content: (
                <>
                    <p>
                        System Sygnalista <strong>gwarantuje pełną anonimowość</strong> zgłaszającego.
                        Zgłoszenia mogą być dokonywane anonimowo — platforma zewnętrzna celowo
                        <strong> nie zapisuje</strong> danych identyfikacyjnych użytkownika, takich jak:
                    </p>
                    <ul>
                        <li>Adres IP zgłaszającego</li>
                        <li>Identyfikator sesji ani ciasteczka śledzące</li>
                        <li>Dane logowania z systemu ITSM</li>
                        <li>Metadane przeglądarki pozwalające na identyfikację</li>
                    </ul>
                    <p>
                        Platforma zewnętrzna (<strong>sygnalista.krosno.com</strong>) działa niezależnie od systemu
                        ITSM — żadne dane z Twojej sesji nie są przekazywane do platformy zgłoszeniowej.
                    </p>
                    <Informator id="sygnalista.okno-prywatne" tytul="Zalecenie">
                        Dla dodatkowego bezpieczeństwa, po kliknięciu
                        „Przejdź do platformy” otwórz link w <strong>oknie prywatnym
                        (incognito)</strong> swojej przeglądarki.
                    </Informator>
                </>
            ),
        },
        {
            key: "who",
            icon: <FiUsers size={18} />,
            title: "Kto może złożyć zgłoszenie?",
            content: (
                <>
                    <p>Z Platformy Sygnalista mogą korzystać:</p>
                    <ul>
                        <li>Pracownicy Spółki zatrudnieni na podstawie umowy o pracę</li>
                        <li>Osoby świadczące pracę na podstawie umów cywilnoprawnych</li>
                        <li>Stażyści i praktykanci</li>
                        <li>Wolontariusze</li>
                        <li>Byli pracownicy Spółki</li>
                        <li>Członkowie organów Spółki</li>
                        <li>Osoby świadczące pracę pod nadzorem i kierownictwem wykonawcy, podwykonawcy lub dostawcy</li>
                    </ul>
                </>
            ),
        },
        {
            key: "what-report",
            icon: <FiAlertTriangle size={18} />,
            title: "Co można zgłosić?",
            content: (
                <>
                    <p>Za pośrednictwem Platformy można zgłaszać naruszenia prawa dotyczące w szczególności:</p>
                    <ul>
                        <li>Korupcji i nadużyć finansowych</li>
                        <li>Naruszeń prawa pracy i zasad BHP</li>
                        <li>Dyskryminacji, mobbingu i molestowania</li>
                        <li>Naruszeń ochrony danych osobowych (RODO)</li>
                        <li>Naruszeń ochrony środowiska</li>
                        <li>Naruszeń bezpieczeństwa produktów i jakości</li>
                        <li>Naruszeń zasad konkurencji i zamówień</li>
                        <li>Prania pieniędzy i finansowania terroryzmu</li>
                        <li>Naruszeń bezpieczeństwa informacji i systemów IT</li>
                    </ul>
                    <div className={styles.warningBox}>
                        <FiAlertTriangle size={14} />
                        <span>
                            Platforma <strong>nie służy</strong> do składania skarg indywidualnych,
                            reklamacji ani zgłoszeń dotyczących spraw pracowniczych nieobjętych
                            zakresem ustawy o ochronie sygnalistów.
                        </span>
                    </div>
                </>
            ),
        },
        {
            key: "protection",
            icon: <FiShield size={18} />,
            title: "Ochrona sygnalisty",
            content: (
                <>
                    <p>
                        Sygnalista, który dokonał zgłoszenia w dobrej wierze, korzysta z <strong>pełnej
                        ochrony</strong> przed działaniami odwetowymi. Zakazane są w szczególności:
                    </p>
                    <ul>
                        <li>Rozwiązanie stosunku pracy lub wypowiedzenie umowy</li>
                        <li>Obniżenie wynagrodzenia lub zmiana warunków zatrudnienia</li>
                        <li>Wstrzymanie awansu lub pominięcie przy przydzielaniu nagród</li>
                        <li>Mobbing, zastraszanie lub wykluczenie społeczne</li>
                        <li>Ujawnienie tożsamości sygnalisty bez jego zgody</li>
                    </ul>
                    <p>
                        Ochrona przysługuje również osobom pomagającym w dokonaniu zgłoszenia
                        oraz osobom powiązanym z sygnalistą.
                    </p>
                </>
            ),
        },
        {
            key: "procedure",
            icon: <FiFileText size={18} />,
            title: "Procedura rozpatrywania zgłoszeń",
            content: (
                <>
                    <p>
                        Każde zgłoszenie jest rejestrowane w Platformie i otrzymuje unikalny numer
                        identyfikacyjny. Proces rozpatrywania obejmuje:
                    </p>
                    <ol>
                        <li>
                            <strong>Potwierdzenie przyjęcia</strong> — w terminie 7 dni od daty
                            otrzymania zgłoszenia.
                        </li>
                        <li>
                            <strong>Postępowanie wyjaśniające</strong> — prowadzone z zachowaniem
                            zasad bezstronności, poufności i rzetelności przez Inspektora ds. Zgodności.
                        </li>
                        <li>
                            <strong>Informacja zwrotna</strong> — o podjętych lub planowanych
                            działaniach następczych, w terminie nie dłuższym niż 3 miesiące.
                        </li>
                    </ol>
                </>
            ),
        },
        {
            key: "external",
            icon: <FiPhone size={18} />,
            title: "Zgłoszenia zewnętrzne",
            content: (
                <>
                    <p>
                        Niezależnie od możliwości dokonania zgłoszenia wewnętrznego za pośrednictwem
                        Platformy, sygnalista ma prawo dokonać <strong>zgłoszenia zewnętrznego</strong> do
                        właściwego organu publicznego, w szczególności do Rzecznika Praw Obywatelskich.
                    </p>
                    <p>
                        Sygnalista może również dokonać <strong>ujawnienia publicznego</strong> w
                        przypadkach określonych w ustawie o ochronie sygnalistów.
                    </p>
                </>
            ),
        },
    ];

    return (
        <div className={styles.page}>
            {/* [U9 2026-09-25] Nagłówek = wspólny NaglowekPodstrony (DS §14): tytuł, zdanie „co tu zrobisz”, liczniki. */}
            <NaglowekPodstrony
                badge="Compliance / Sygnalista"
                title="Sygnalista"
                subtitle={`Zgłoś anonimowo nieprawidłowość w ${companyName} — zgodnie z ustawą o ochronie sygnalistów Twoja tożsamość jest chroniona.`}
                stats={[
                    { key: "anonimowo", value: "Anonimowo", label: "bez IP i sesji", icon: <FiEyeOff />, color: "#34D399", title: "Zgłoszenie nie zapisuje danych identyfikujących" },
                    { key: "ochrona", value: "Ochrona", label: "zakaz odwetu", icon: <FiShield />, color: "#60A5FA", title: "Ochrona przed działaniami odwetowymi" },
                    { key: "termin", value: "3 miesiące", label: "na odpowiedź", icon: <FiFileText />, color: "#FBBF24", title: "Termin informacji zwrotnej" },
                ]}
            />

            {/* Top row — 3 feature cards */}
            <div className={styles.featureRow}>
                <motion.div className={styles.featureCard} custom={1} variants={fadeUp} initial="hidden" animate="visible">
                    <div className={styles.featureIcon}>
                        <FiEyeOff size={22} />
                    </div>
                    <h3 className={styles.featureTitle}>Pełna anonimowość</h3>
                    <p className={styles.featureDesc}>
                        System celowo nie zapisuje adresu IP, identyfikatora sesji, ciasteczek śledzących
                        ani metadanych przeglądarki. Platforma zewnętrzna działa niezależnie od ITSM.
                    </p>
                </motion.div>

                <motion.div className={styles.featureCard} custom={2} variants={fadeUp} initial="hidden" animate="visible">
                    <div className={styles.featureIcon}>
                        <FiLock size={22} />
                    </div>
                    <h3 className={styles.featureTitle}>Ochrona prawna</h3>
                    <p className={styles.featureDesc}>
                        Pełna ochrona przed działaniami odwetowymi, zgodnie z ustawą z dnia 14 czerwca
                        2024 r. o ochronie sygnalistów. Zakaz zwolnienia, mobbingu i dyskryminacji.
                    </p>
                </motion.div>

                <motion.div className={styles.featureCard} custom={3} variants={fadeUp} initial="hidden" animate="visible">
                    <div className={styles.featureIcon}>
                        <FiGlobe size={22} />
                    </div>
                    <h3 className={styles.featureTitle}>Dostęp z każdego miejsca</h3>
                    <p className={styles.featureDesc}>
                        Platforma jest dostępna zawsze pod adresem{" "}
                        <a href={PLATFORM_URL} target="_blank" rel="noopener noreferrer" className={styles.featureLink}>
                            sygnalista.krosno.com
                        </a>{" "}
                        — nie tylko z systemu ITSM, ale z dowolnego urządzenia.
                    </p>
                </motion.div>
            </div>

            {/* Main grid — 2 columns */}
            <div className={styles.mainGrid}>
                {/* Left column — CTA + privacy */}
                <div className={styles.leftCol}>
                    <motion.div className={styles.ctaCard} custom={4} variants={fadeUp} initial="hidden" animate="visible">
                        <div className={styles.ctaShield}>
                            <FiShield size={36} />
                        </div>
                        <h2 className={styles.ctaTitle}>Zgłoś naruszenie prawa</h2>
                        <p className={styles.ctaDesc}>
                            Platforma gwarantuje pełną anonimowość. Twoja tożsamość jest chroniona,
                            a zgłoszenie zostanie przekazane do wyznaczonej osoby odpowiedzialnej
                            za rozpatrywanie naruszeń.
                        </p>
                        <button className={styles.ctaButton} onClick={handleOpenPlatform}>
                            <FiExternalLink size={16} />
                            Przejdź do platformy
                        </button>
                        {/* Strona zaleca okno prywatne — bez kopiowania adresu ta rada była niewykonalna. */}
                        <button type="button" className={styles.ctaSecondaryBtn} onClick={handleCopyUrl}>
                            <FiCopy size={14} /> Kopiuj adres do okna prywatnego
                        </button>
                        <div className={styles.ctaBadges}>
                            <span className={styles.ctaBadge}><FiEyeOff size={12} /> Anonimowe</span>
                            <span className={styles.ctaBadge}><FiLock size={12} /> Szyfrowane</span>
                            <span className={styles.ctaBadge}><FiShield size={12} /> Chronione</span>
                        </div>
                    </motion.div>

                    <motion.div className={styles.privacyCard} custom={5} variants={fadeUp} initial="hidden" animate="visible">
                        <div className={styles.privacyHeader}>
                            <div className={styles.privacyIcon}>
                                <FiEye size={18} />
                            </div>
                            <h3 className={styles.privacyTitle}>Tryb prywatny przeglądarki</h3>
                        </div>
                        <p className={styles.privacyDesc}>
                            Dla zachowania maksymalnej anonimowości, zalecamy otwieranie platformy
                            w <strong>oknie prywatnym (incognito)</strong> Twojej przeglądarki.
                            Dzięki temu żadne dane sesji, ciasteczka ani historia przeglądania
                            nie będą przechowywane na Twoim urządzeniu.
                        </p>
                        <div className={styles.privacyShortcuts}>
                            <span className={styles.shortcutItem}>
                                <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>N</kbd>
                                <span className={styles.shortcutLabel}>Chrome / Edge</span>
                            </span>
                            <span className={styles.shortcutItem}>
                                <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>P</kbd>
                                <span className={styles.shortcutLabel}>Firefox</span>
                            </span>
                        </div>
                    </motion.div>

                    {/* Legal footer card */}
                    <motion.div className={styles.legalCard} custom={6} variants={fadeUp} initial="hidden" animate="visible">
                        <p className={styles.legalText}>
                            Podstawa prawna: Ustawa z dnia 14 czerwca 2024 r. o ochronie sygnalistów
                            (Dz. U. z 2024 r. poz. 928). Regulamin obowiązuje od 14.02.2026 r.
                        </p>
                        {/* Dane rejestrowe pracodawcy z konfiguracji firmy — nie zaszywać w kodzie */}
                        {companyLegalLine && (
                            <p className={styles.legalCompany}>{companyLegalLine}</p>
                        )}
                    </motion.div>
                </div>

                {/* Right column — Accordion */}
                <motion.div className={styles.rightCol} custom={4} variants={fadeUp} initial="hidden" animate="visible">
                    <div className={styles.accordionCard}>
                        <div className={styles.accordionCardHeader}>
                            <FiHelpCircle size={18} />
                            <h2 className={styles.accordionCardTitle}>Najważniejsze informacje</h2>
                        </div>

                        <div className={styles.accordion}>
                            {infoSections.map((section) => (
                                <div
                                    key={section.key}
                                    className={`${styles.accordionItem} ${expandedSection === section.key ? styles.accordionItemOpen : ""}`}
                                >
                                    <button
                                        className={styles.accordionHeader}
                                        onClick={() => toggleSection(section.key)}
                                        type="button"
                                        aria-expanded={expandedSection === section.key}
                                        aria-controls={`sygnalista-sec-${section.key}`}
                                    >
                                        <span className={styles.accordionIcon} aria-hidden="true">{section.icon}</span>
                                        <span className={styles.accordionTitle}>{section.title}</span>
                                        <FiChevronDown
                                            size={15}
                                            aria-hidden="true"
                                            className={`${styles.accordionChevron} ${expandedSection === section.key ? styles.accordionChevronOpen : ""}`}
                                        />
                                    </button>
                                    <AnimatePresence>
                                        {expandedSection === section.key && (
                                            <motion.div
                                                className={styles.accordionBody}
                                                id={`sygnalista-sec-${section.key}`}
                                                role="region"
                                                aria-label={section.title}
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                                            >
                                                <div className={styles.accordionContent}>
                                                    {section.content}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Redirect countdown overlay */}
            <AnimatePresence>
                {showRedirectOverlay && (
                    <motion.div
                        className={styles.redirectOverlay}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        onClick={(e) => { if (e.target === e.currentTarget) shakeModal(overlayCardRef.current); }}
                    >
                        <motion.div
                            className={styles.redirectCard}
                            ref={overlayCardRef}
                            tabIndex={-1}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="sygnalista-redirect-title"
                            initial={{ opacity: 0, scale: 0.92, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                        >
                            <button
                                type="button"
                                className={styles.redirectCloseBtn}
                                onClick={handleCancelRedirect}
                                aria-label="Zamknij okno przekierowania"
                            >
                                <FiX size={16} />
                            </button>

                            <div className={styles.redirectShieldIcon}>
                                <FiShield size={28} />
                            </div>

                            <h2 className={styles.redirectTitle} id="sygnalista-redirect-title">
                                Przekierowanie na zewnętrzny serwis
                            </h2>

                            <span className={styles.redirectSubtitle}>
                                Za chwilę zostaniesz przekierowany na platformę{" "}
                                <strong>sygnalista.krosno.com</strong>
                            </span>

                            <div className={styles.redirectCountdown}>
                                <div className={styles.countdownCircle}>
                                    <svg viewBox="0 0 48 48" className={styles.countdownSvg}>
                                        <circle
                                            cx="24" cy="24" r="20"
                                            fill="none"
                                            stroke="var(--color-border)"
                                            strokeWidth="3"
                                        />
                                        <circle
                                            cx="24" cy="24" r="20"
                                            fill="none"
                                            stroke="var(--color-primary)"
                                            strokeWidth="3"
                                            strokeDasharray={2 * Math.PI * 20}
                                            strokeDashoffset={2 * Math.PI * 20 * (1 - countdown / COUNTDOWN_SECONDS)}
                                            strokeLinecap="round"
                                            className={styles.countdownProgress}
                                        />
                                    </svg>
                                    <span className={styles.countdownNumber} aria-live="polite">{countdown}</span>
                                </div>
                            </div>

                            {popupBlocked && (
                                <div className={styles.redirectBlocked} role="alert">
                                    <FiAlertTriangle size={14} />
                                    <span>
                                        Przeglądarka zablokowała automatyczne otwarcie nowej karty.{" "}
                                        <a href={PLATFORM_URL} target="_blank" rel="noopener noreferrer" onClick={handleCancelRedirect}>
                                            Otwórz platformę ręcznie
                                        </a>.
                                    </span>
                                </div>
                            )}

                            <div className={styles.redirectBadges}>
                                <span className={styles.redirectBadge}><FiEyeOff size={12} /> Anonimowe</span>
                                <span className={styles.redirectBadge}><FiLock size={12} /> Szyfrowane</span>
                                <span className={styles.redirectBadge}><FiShield size={12} /> Chronione</span>
                            </div>

                            <span className={styles.redirectHint}>
                                Zalecamy otworzyć link w <strong>oknie prywatnym (incognito)</strong> dla
                                maksymalnej anonimowości
                            </span>

                            <div className={styles.redirectActions}>
                                <button
                                    className={styles.redirectCancelBtn}
                                    onClick={handleCancelRedirect}
                                    type="button"
                                >
                                    Anuluj
                                </button>
                                <button
                                    className={styles.redirectCopyBtn}
                                    onClick={handleCopyUrl}
                                    type="button"
                                    title="Skopiuj adres, żeby wkleić go w oknie prywatnym"
                                >
                                    <FiCopy size={13} /> Kopiuj adres
                                </button>
                                <button
                                    className={styles.redirectGoBtn}
                                    onClick={handleGoNow}
                                    type="button"
                                >
                                    <FiArrowRight size={14} /> Przejdź teraz
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SygnalistaPage;
